/**
 * Protected Route Component
 * Handles route protection and loading states
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, authError } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useRef(null);

  // Check authentication and redirect if necessary
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Show toast notification
      if (toast.current) {
        toast.current.show({
          severity: 'warn',
          summary: 'Session Expired',
          detail: 'Logged out due to inactivity',
          life: 3000
        });
      }
      
      // Redirect to login after a short delay to allow toast to show
      const timer = setTimeout(() => {
        navigate('/login', { state: { from: location }, replace: true });
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, isLoading, navigate, location]);

  // Show loading spinner while checking authentication
  if (isLoading) {
    return <LoadingSpinner message="Verifying authentication..." />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <div>
        <Toast ref={toast} />
      </div>
    );
  }

  // Show error message if there's an auth error (but still authenticated)
  if (authError && isAuthenticated) {
    return (
      <div className="min-h-screen">
        <div className="p-3">
          <Message
            severity="warn"
            text={authError}
            className="w-full mb-3"
          />
        </div>
        {children}
      </div>
    );
  }

  // Render protected content
  return children;
};

export default ProtectedRoute;